import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, User, LogIn, Sparkles } from 'lucide-react';
import BrandLogo from './BrandLogo';
import PremiumModal from './PremiumModal';
import { useTVMode } from '../utils/tvMode';
import { getPlatform } from '../utils/capacitor';

const AppShell = ({ user, isPro, signInWithGoogle, logoutUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isTV = useTVMode();

  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const isHome = location.pathname === '/' || location.pathname === '/profile';
  const isPlayer = location.pathname === '/player';

  // Android hardware back — navigate within app instead of exiting
  useEffect(() => {
    if (getPlatform() !== 'android') return;
    const onBack = () => {
      if (!isHome) navigate(-1);
      // On home: let Android handle it (exits app)
    };
    document.addEventListener('ionBackButton', onBack);
    return () => document.removeEventListener('ionBackButton', onBack);
  }, [isHome, navigate]);

  // Hide the shell nav entirely while the player is active
  if (isPlayer) {
    return (
      <div className="app-shell">
        <Outlet context={{ user, isPro }} />
      </div>
    );
  }

  return (
    <div className="app-shell">

      {/* ── TOP NAV BAR ── */}
      <nav className="app-shell-nav">

        {/* Back button — shown on every screen except home */}
        {!isHome ? (
          <button
            className="app-nav-icon-btn"
            onClick={() => navigate(-1)}
            aria-label="Back"
            tabIndex={0}
          >
            <ArrowLeft size={isTV ? 28 : 22} />
          </button>
        ) : (
          /* Placeholder keeps logo centred on home */
          <div style={{ width: isTV ? 48 : 40 }} />
        )}

        {/* Logo — taps to home */}
        <div
          className="app-nav-logo"
          onClick={() => navigate('/')}
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
          role="button"
          aria-label="Home"
        >
          <BrandLogo compact />
        </div>

        {/* Right side */}
        <div className="app-nav-right">
          {user ? (
            <>
              {isPro && (
                <span className="app-nav-pro-badge">PRO</span>
              )}

              {!isPro && (
                <button
                  className="app-nav-upgrade-btn"
                  onClick={() => setShowPremiumModal(true)}
                  tabIndex={0}
                >
                  <Sparkles size={14} />
                  <span>Go Pro</span>
                </button>
              )}

              <button
                className="app-nav-icon-btn"
                onClick={() => navigate('/profile')}
                aria-label="Profile"
                tabIndex={0}
              >
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="Profile"
                    className="app-nav-avatar"
                  />
                ) : (
                  <User size={isTV ? 26 : 20} />
                )}
              </button>
            </>
          ) : (
            <button
              className="app-nav-signin-btn"
              onClick={signInWithGoogle}
              tabIndex={0}
            >
              <LogIn size={16} />
              <span>Sign In</span>
            </button>
          )}
        </div>
      </nav>

      {/* ── PAGE CONTENT ── */}
      <main className="app-shell-content">
        <Outlet context={{ user, isPro }} />
      </main>

      {showPremiumModal && (
        <PremiumModal
          user={user}
          onClose={() => setShowPremiumModal(false)}
          trigger="app-shell"
        />
      )}
    </div>
  );
};

export default AppShell;
